// Copyright (c) 2025 Signal from Noise, Inc. All rights reserved.
//
// This software is the confidential and proprietary information of
// Signal from Noise, Inc. You shall not disclose such Confidential Information
// and shall use it only in accordance with the terms of a separate license
// agreement entered into with Signal from Noise, Inc.
//
// Authors: /AUTHORS

import SparkMD5 from "spark-md5";

/**
 * Get the backend API URL based on the current environment
 */
export const getBackendURL = (): string => {
  if (typeof window === "undefined") return "http://localhost:3443";

  const env = RUNNING_ENV;
  if (env === "local") return "http://localhost:3443";
  if (env === "development") return "https://api.storytell-development.ai";
  if (env === "staging") return "https://api.storytell-staging.ai";
  return "https://api.storytell.ai";
};

/**
 * Response from saving an artifact to Knowledge Base
 */
export interface SaveArtifactResponse {
  assetId: string;
}

/**
 * Error class for artifact save operations
 */
export class ArtifactSaveError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public originalError?: unknown,
  ) {
    super(message);
    this.name = "ArtifactSaveError";
  }
}

/**
 * Utility functions for hex to base64 conversion
 */
function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = Number.parseInt(hex.substring(i, i + 2), 16);
  }
  return bytes;
}

function binaryToBase64(binary: string): string {
  return btoa(binary);
}

function hexToBase64(hexString: string): string {
  const bytes = hexToBytes(hexString);
  const binaryString = Array.from(bytes)
    .map((byte) => String.fromCharCode(byte))
    .join("");
  return binaryToBase64(binaryString);
}

/**
 * Calculate MD5 hash of a blob in base64 format (for GCP compatibility)
 */
async function calculateMD5(blob: Blob): Promise<string> {
  const arrayBuffer = await blob.arrayBuffer();
  const spark = SparkMD5.ArrayBuffer.hash(arrayBuffer);
  return hexToBase64(spark);
}

/**
 * Save artifact as PDF to user's Knowledge Base using standard asset upload flow
 *
 * @param pdfBlob - PDF file blob to upload
 * @param displayName - Display name for the asset
 * @param authToken - Authentication token for API request
 * @returns Promise resolving to asset ID
 * @throws ArtifactSaveError with user-friendly message
 */
export async function saveArtifactToKB(
  pdfBlob: Blob,
  displayName: string,
  authToken: string,
): Promise<SaveArtifactResponse> {
  const filename = `${displayName}.pdf`;
  const md5Hash = await calculateMD5(pdfBlob);

  try {
    // Create asset metadata
    const createResponse = await fetch(`${getBackendURL()}/assets`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        filename,
        fileSizeInBytes: pdfBlob.size,
        mimeType: "application/pdf",
        md5: md5Hash,
      }),
    });

    if (!createResponse.ok) {
      throw new ArtifactSaveError("Failed to create asset", createResponse.status);
    }

    const createData = await createResponse.json();
    const assetId = createData.id;
    const uploadMethod = createData.method;
    const uploadUrl = createData.signedUrl;

    // Upload PDF blob
    if (uploadMethod === "DIRECT") {
      const uploadFormData = new FormData();
      uploadFormData.append("file", pdfBlob, filename);

      const uploadResponse = await fetch(`${getBackendURL()}/assets/${assetId}/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${authToken}` },
        body: uploadFormData,
      });

      if (!uploadResponse.ok) {
        throw new ArtifactSaveError("Failed to upload file", uploadResponse.status);
      }
    } else if (uploadMethod === "SIGNED_URL") {
      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        body: pdfBlob,
      });

      if (!uploadResponse.ok) {
        throw new ArtifactSaveError("Failed to upload file", uploadResponse.status);
      }
    }

    // Mark upload complete (triggers ingestion)
    const completeResponse = await fetch(`${getBackendURL()}/assets/${assetId}/uploaded`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${authToken}` },
    });

    if (!completeResponse.ok) {
      throw new ArtifactSaveError("Failed to complete upload", completeResponse.status);
    }

    // Get system artifact label
    const labelResponse = await fetch(`${getBackendURL()}/labels/system/artifact`, {
      method: "GET",
      headers: { Authorization: `Bearer ${authToken}` },
    });

    if (!labelResponse.ok) {
      throw new ArtifactSaveError("Failed to get artifact label", labelResponse.status);
    }

    const labelData = await labelResponse.json();
    const labelId = labelData.id;

    // Associate label with asset
    const associateResponse = await fetch(`${getBackendURL()}/label-associations`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify([
        {
          labelId,
          resourceId: assetId,
          resourceType: "asset",
        },
      ]),
    });

    if (!associateResponse.ok) {
      // Label association failure is not critical - asset is already uploaded
      console.warn("Failed to associate label with artifact");
    }

    return { assetId };
  } catch (error) {
    if (error instanceof ArtifactSaveError) {
      throw error;
    }

    if (error instanceof TypeError) {
      throw new ArtifactSaveError("Network error, please check your connection", undefined, error);
    }

    throw new ArtifactSaveError("Unexpected error occurred", undefined, error);
  }
}
